import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import { Icon, IconType } from '../../Atoms/Icon';

export interface CopyButtonProps {
  value: string;
  ariaLabel?: string;
  dataTestId?: string;
}

type CopyStatus = 'idle' | 'copied' | 'error';

const STATUS_ICON: Record<CopyStatus, IconType> = {
  idle: 'copy',
  copied: 'greenCheckmark',
  error: 'alert'
};

export const CopyButton = ({ value, ariaLabel, dataTestId }: CopyButtonProps) => {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setStatus('copied');
    } catch {
      setStatus('error');
    }
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setStatus('idle'), 2000);
  };

  // The accessible name stays stable; copy status is announced once via the live region below to
  // avoid a double announcement on the already-focused button.
  const label = ariaLabel || 'Copy to clipboard';
  const liveMessage = status === 'copied' ? 'Copied to clipboard' : status === 'error' ? 'Copy failed' : '';

  return (
    <>
      <IconButton
        aria-label={label}
        title={status === 'error' ? 'Copy failed — select the text and copy manually' : undefined}
        data-testid={dataTestId}
        sx={{
          border: '1px solid #D3D3D3',
          borderRadius: '4px',
          marginTop: '-2px'
        }}
        onClick={handleCopy}>
        <Icon type={STATUS_ICON[status]} />
      </IconButton>
      <span
        role="status"
        className="sr-only">
        {liveMessage}
      </span>
    </>
  );
};
